import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Input, Space, Spin, Empty, Image } from 'antd';
import Title from 'antd/lib/typography/Title';
import { SearchOutlined } from '@ant-design/icons';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { TextService } from '../../Services/TextService';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import NotFound from '../../Components/Page/NotFoundPage';
import { WebApiPublicSiteDtoPublicMemberViewResultDto, WebApiPublicSiteDtoPublicViewColumnDto, WebApiCompanyDtoCompanyPublicSiteDto } from '../../Repository/eventbookyapi';
import { PublicSiteMenu } from '../../Components/PublicSite/PublicSiteMenu';
import { mapDynamicInputLabel } from '../../Mappers/mapDynamicInputLabel';
import { MemberFieldTypes } from '../../Types/MemberFieldTypes';

const { Search } = Input;

// Map MemberFieldTypes to actual property names in member data
const getPropertyNameFromColumnType = (columnType: number): string => {
    switch (columnType) {
        case MemberFieldTypes.UniqueId: return 'UniqueId';
        case MemberFieldTypes.FirstName: return 'FirstName';
        case MemberFieldTypes.LastName: return 'LastName';
        case MemberFieldTypes.Email: return 'Email';
        case MemberFieldTypes.Email2: return 'Email2';
        case MemberFieldTypes.Address: return 'Address';
        case MemberFieldTypes.PostalArea: return 'PostalArea';
        case MemberFieldTypes.PostalCode: return 'PostalCode';
        case MemberFieldTypes.Country: return 'Country';
        case MemberFieldTypes.PhoneNumber: return 'PhoneNumber';
        case MemberFieldTypes.PhoneNumber2: return 'PhoneNumber2';
        case MemberFieldTypes.Title: return 'Title';
        case MemberFieldTypes.Company: return 'Company';
        case MemberFieldTypes.Extra1: return 'Extra1';
        case MemberFieldTypes.Extra2: return 'Extra2';
        case MemberFieldTypes.Extra3: return 'Extra3';
        case MemberFieldTypes.PersonalNumber: return 'PersonalNumber';
        case MemberFieldTypes.AdressRow2: return 'AdressRow2';
        case MemberFieldTypes.AdressRow3: return 'AdressRow3';
        case MemberFieldTypes.JoinedDate: return 'JoinedDate';
        case MemberFieldTypes.WebPage: return 'WebPage';
        default: return `CustomField_${columnType}`;
    }
};

const PublicSiteMemberSearch = () => {
    const GetText = TextService.GetText;
    const params = useParams();

    const [loading, setLoading] = useState(true);
    const [viewData, setViewData] = useState<WebApiPublicSiteDtoPublicMemberViewResultDto | null>(null);
    const [filteredMembers, setFilteredMembers] = useState<any[]>([]);
    const [searchText, setSearchText] = useState('');
    const [companyNotFound, setCompanyNotFound] = useState(false);
    const [publicSite, setPublicSite] = useState<WebApiCompanyDtoCompanyPublicSiteDto | null>(null);

    useEffect(() => {
        const loadMemberView = async () => {
            if (params.companyurl) {
                try {
                    setLoading(true);

                    // Load company public site data
                    const companyData = await PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl);
                    setPublicSite(companyData);

                    const hasView = await PublicSiteService.HasPublicMemberView(params.companyurl);

                    if (!hasView) {
                        setCompanyNotFound(true);
                        return;
                    }

                    const result = await PublicSiteService.GetPublicMemberView(params.companyurl);
                    setViewData(result);
                    setFilteredMembers(result.members || []);
                } catch (error) {
                    console.error('Error loading member view:', error);
                    setCompanyNotFound(true);
                } finally {
                    setLoading(false);
                }
            }
        };

        loadMemberView();
    }, [params.companyurl]);

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    const handleSearch = (value: string) => {
        setSearchText(value);

        if (!value.trim()) {
            setFilteredMembers(viewData?.members || []);
            return;
        }

        const searchLower = value.toLowerCase();
        const filtered = (viewData?.members || []).filter((member: any) => {
            return Object.values(member).some((fieldValue: any) => {
                if (fieldValue == null) return false;
                return String(fieldValue).toLowerCase().includes(searchLower);
            });
        });

        setFilteredMembers(filtered);
    };

    if (companyNotFound) {
        return <NotFound />;
    }

    if (loading) {
        return (
            <PagePublicSite companyUrl={params.companyurl!}>
                <div style={{ textAlign: 'center', padding: '100px' }}>
                    <Spin size="large" />
                </div>
            </PagePublicSite>
        );
    }

    if (!viewData || !viewData.columns || viewData.columns.length === 0) {
        return (
            <PagePublicSite companyUrl={params.companyurl!}>
                {publicSite && (
                    <>
                        {publicSite.siteLogoPath ? (
                            <div style={{ marginBottom: "20px", textAlign: "center" }}>
                                <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                            </div>
                        ) : (
                            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                                <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                            </div>
                        )}
                        <PublicSiteMenu />
                    </>
                )}
                <Empty description={GetText('PublicSiteMemberSearch_NoViewConfigured')} />
            </PagePublicSite>
        );
    }

    // Generate table columns from view configuration
    const columns = (viewData.columns || []).map((col: WebApiPublicSiteDtoPublicViewColumnDto) => {
        const propertyName = getPropertyNameFromColumnType(col.columnType!);

        return {
            title: mapDynamicInputLabel(col.columnType as any, [], col.customFieldId) || 'Field',
            dataIndex: propertyName,
            key: propertyName,
            render: (value: any) => value || '-',
            sorter: (a: any, b: any) => {
                const aVal = String(a[propertyName] || '');
                const bVal = String(b[propertyName] || '');
                return aVal.localeCompare(bVal);
            }
        };
    });

    return (
        <PagePublicSite companyUrl={params.companyurl!}>
            {publicSite && (
                <>
                    {publicSite.siteLogoPath ? (
                        <div style={{ marginBottom: "20px", textAlign: "center" }}>
                            <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", marginBottom: "20px" }}>
                            <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                        </div>
                    )}
                    <PublicSiteMenu />
                </>
            )}

            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 20px' }}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <h2>{GetText('PublicSiteMemberSearch_Title')}</h2>
                        <Search
                            placeholder={GetText('PublicSiteMemberSearch_SearchPlaceholder')}
                            allowClear
                            enterButton={<SearchOutlined />}
                            size="large"
                            onSearch={handleSearch}
                            onChange={(e) => handleSearch(e.target.value)}
                            value={searchText}
                            style={{ marginBottom: 16 }}
                        />
                    </div>

                    <Table
                        columns={columns}
                        dataSource={filteredMembers}
                        rowKey={(record: any) => record.MemberId || record.memberId}
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} ${GetText('Common_Of')} ${total} ${GetText('PublicSiteMemberSearch_Members')}`,
                        }}
                        scroll={{ x: true }}
                        locale={{
                            emptyText: <Empty description={GetText('PublicSiteMemberSearch_NoResults')} />
                        }}
                    />
                </Space>
            </div>
        </PagePublicSite>
    );
};

export default PublicSiteMemberSearch;
